/**
 * Predictive search overlay — popular searches, suggest fetch, no-results, keyboard.
 */
(() => {
  const TAG = 'predictive-search';
  const DEBOUNCE_MS = 100;
  const CACHE_LIMIT = 40;
  const MIN_CHARS_DEFAULT = 2;
  const MIN_CHARS_FLOOR = 2;
  const MIN_CHARS_CEILING = 6;
  const OPEN_EVENT = 'predictive_search_open';
  const CLOSE_EVENT = 'predictive_search_close';
  const OPTION_SELECTOR = '[data-predictive-option]';

  if (customElements.get(TAG)) return;

  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  class PredictiveSearch extends HTMLElement {
    #open = false;
    #abort = null;
    #timer = 0;
    #activeIndex = -1;
    #scrollLocked = false;
    #requestToken = 0;
    #paintedToken = 0;
    #lastQuery = '';
    #cache = new Map();
    #countCache = new Map();

    #collectionBound = false;
    #resolvedQuery = null;
    /* Outside tap closes the panel but leaves the caret in the field. Hold it
       shut until the next deliberate click or keystroke, otherwise the click
       that dismissed it opens it again. */
    #dismissed = false;

    connectedCallback() {
      this.#bindViewAll();
      this.#bindCollectionCarousel();
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('input', this.#onInput);
      this.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('click', this.#onClick);
      this.addEventListener('submit', this.#onSubmit);
      document.addEventListener('pointerdown', this.#onDocumentPointer);
      document.addEventListener(CLOSE_EVENT, this.#onCloseEvent);
      document.addEventListener(OPEN_EVENT, this.#onOpenEvent);
      window.addEventListener('resize', this.#onResize);
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.#onFocusIn);
      this.removeEventListener('input', this.#onInput);
      this.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('submit', this.#onSubmit);
      document.removeEventListener('pointerdown', this.#onDocumentPointer);
      document.removeEventListener(CLOSE_EVENT, this.#onCloseEvent);
      document.removeEventListener(OPEN_EVENT, this.#onOpenEvent);
      window.removeEventListener('resize', this.#onResize);
      this.#collectionScroller()?.removeEventListener('scroll', this.#onCollectionScroll);
      this.#collectionDots()?.removeEventListener('click', this.#onCollectionDotClick);
      this.#collectionBound = false;
      this.#abortFetches();
      window.clearTimeout(this.#timer);
      this.#unlockScroll();
    }

    #input() {
      return this.querySelector('[data-header-search-input]');
    }

    #panel() {
      return this.querySelector('[data-predictive-panel]');
    }

    #backdrop() {
      return this.querySelector('[data-predictive-backdrop]');
    }

    #empty() {
      return this.querySelector('[data-predictive-empty]');
    }

    #results() {
      return this.querySelector('[data-predictive-results]');
    }

    #noResults() {
      return this.querySelector('[data-predictive-no-results]');
    }

    #viewAll() {
      return this.querySelector('[data-predictive-view-all]');
    }

    #viewAllBar() {
      return this.querySelector('.predictive-search__view-all-bar');
    }

    #bindViewAll() {
      const bar = this.querySelector('.predictive-search__view-all-button');

      if (!(bar instanceof HTMLAnchorElement)) return;

      bar.setAttribute('role', 'option');
      bar.setAttribute('data-predictive-option', '');
      bar.setAttribute('data-predictive-view-all', '');

      if (!bar.id) {
        bar.id = `PredictiveSearchViewAll-${this.#input()?.id || 'search'}`;
      }
    }

    #collectionScroller() {
      return this.querySelector('[data-predictive-collection-scroller]');
    }

    #collectionDots() {
      return this.querySelector('[data-predictive-collection-dots]');
    }

    #bindCollectionCarousel() {
      if (this.#collectionBound) return;

      const scroller = this.#collectionScroller();
      const dots = this.#collectionDots();

      if (!scroller || !dots) return;

      this.#collectionBound = true;
      scroller.addEventListener('scroll', this.#onCollectionScroll, { passive: true });
      dots.addEventListener('click', this.#onCollectionDotClick);
    }

    #onCollectionScroll = () => {
      this.#updateCollectionDot();
    };

    #onCollectionDotClick = (event) => {
      const button =
        event.target instanceof Element ? event.target.closest('[data-predictive-collection-dot]') : null;

      if (!button) return;

      event.preventDefault();

      const index = Number(button.getAttribute('data-index'));
      const scroller = this.#collectionScroller();
      const cards = scroller?.querySelectorAll('.predictive-search-collection');
      const card = cards && Number.isFinite(index) ? cards[index] : null;

      if (!(scroller instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

      const left =
        card.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;

      scroller.scrollTo({ left, behavior: 'smooth' });
    };

    #syncCollectionDots() {
      const scroller = this.#collectionScroller();
      const dots = this.#collectionDots();

      if (!(scroller instanceof HTMLElement) || !(dots instanceof HTMLElement)) return;

      const cards = [...scroller.querySelectorAll('.predictive-search-collection')];
      const overflow = scroller.scrollWidth - scroller.clientWidth > 8;

      dots.replaceChildren();

      if (isDesktop() || !overflow || cards.length < 2) {
        dots.hidden = true;
        return;
      }

      const labelTemplate = dots.getAttribute('data-dot-label') || '';

      for (const [index, card] of cards.entries()) {
        if (!(card instanceof HTMLElement)) continue;

        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'predictive-search__collection-dot';
        button.setAttribute('data-predictive-collection-dot', '');
        button.setAttribute('data-index', String(index));
        button.setAttribute('aria-label', labelTemplate.replaceAll('INDEX', String(index + 1)));
        dots.appendChild(button);
      }

      dots.hidden = false;
      this.#updateCollectionDot();
    }

    #updateCollectionDot() {
      const scroller = this.#collectionScroller();
      const dots = this.#collectionDots();

      if (!(scroller instanceof HTMLElement) || !(dots instanceof HTMLElement)) return;

      const cards = [...scroller.querySelectorAll('.predictive-search-collection')];
      const buttons = [...dots.querySelectorAll('[data-predictive-collection-dot]')];
      const origin = scroller.getBoundingClientRect().left;
      let active = 0;

      for (const [index, card] of cards.entries()) {
        if (!(card instanceof HTMLElement)) continue;
        if (card.getBoundingClientRect().left - origin <= 12) active = index;
      }

      for (const [index, button] of buttons.entries()) {
        const current = index === active;

        button.classList.toggle('is-active', current);
        if (current) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      }
    }

    #header() {
      return this.closest('site-header');
    }

    #minChars() {
      const value = Number(this.getAttribute('data-min-chars'));

      if (!Number.isFinite(value)) return MIN_CHARS_DEFAULT;

      return Math.min(MIN_CHARS_CEILING, Math.max(MIN_CHARS_FLOOR, Math.trunc(value)));
    }

    #visibleOptions(root) {
      if (!root) return [];

      return [...root.querySelectorAll(OPTION_SELECTOR)].filter(
        (node) => node instanceof HTMLElement && !node.hidden && node.getClientRects().length > 0
      );
    }

    #options() {
      const view = this.getAttribute('data-view');

      if (view === 'no-results') {
        return this.#visibleOptions(this.#noResults());
      }

      const empty = this.#empty();
      const results = this.#results();
      const source =
        results && !results.hidden ? results : empty && !empty.hidden ? empty : this;
      const options = this.#visibleOptions(source);
      const viewAll = this.#viewAll();

      if (
        viewAll instanceof HTMLElement &&
        !viewAll.hidden &&
        viewAll.getClientRects().length > 0 &&
        !options.includes(viewAll)
      ) {
        options.push(viewAll);
      }

      return options;
    }

    // Tabbing onto the field only focuses it. Opening here hides the nav, so
    // the panel waits for a click, typing, or an arrow key.
    #onFocusIn = (event) => {
      if (event.target !== this.#input()) return;

      this.#syncQueryState();
    };

    #onInput = () => {
      this.#dismissed = false;
      this.#syncQueryState();

      if (!this.#open) {
        this.#show();
        return;
      }

      this.#scheduleFetch();
    };

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-predictive-clear]')) {
        event.preventDefault();
        this.#clearQuery();
        return;
      }

      if (target.closest('[data-predictive-close]')) {
        event.preventDefault();
        this.#dismissed = false;
        this.#hide(true, true);
        return;
      }

      if (target.closest('[data-predictive-backdrop]')) {
        event.preventDefault();
        this.#dismissed = this.#input() === document.activeElement;
        this.#hide(false);
        return;
      }

      if (target.closest('.header-search') && !target.closest('button, a')) {
        if (this.#dismissed) return;

        this.#input()?.focus();
        this.#show();
      }
    };

    #onSubmit = (event) => {
      event.preventDefault();
      this.#hide(false);
      window.location.assign(this.#resultsUrl());
    };

    #onDocumentPointer = (event) => {
      const target =
        event.target instanceof Element
          ? event.target
          : event.target instanceof Node
            ? event.target.parentElement
            : null;
      const onField = Boolean(target?.closest('.header-search')) && target && this.contains(target);

      if (!this.#open) {
        if (onField) this.#dismissed = false;
        return;
      }

      if (this.contains(event.target)) return;

      this.#dismissed = this.#input() === document.activeElement;
      this.#hide(false);
    };

    #onCloseEvent = (event) => {
      const reset = event.detail?.reset === true;

      if (reset) this.#dismissed = false;

      if (this.#open) {
        this.#hide(false, reset);
        return;
      }

      if (!reset) return;

      const input = this.#input();

      if (input) input.value = '';

      this.#lastQuery = '';
      this.#syncQueryState();
      this.#showEmpty();
    };

    #onOpenEvent = (event) => {
      if (event.target !== this && this.#open) this.#hide(false);
    };

    #onResize = () => {
      if (!this.#open) return;
      this.#syncDialogMode();
      if (this.getAttribute('data-view') === 'no-results') this.#syncCollectionDots();
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape') {
        if (!this.#open) return;

        event.preventDefault();
        event.stopPropagation();
        this.#hide(true, true);
        return;
      }

      if (
        !this.#open &&
        (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
        event.target === this.#input() &&
        this.#canOpen()
      ) {
        event.preventDefault();
        this.#dismissed = false;
        this.#show();
        this.#moveActive(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (!this.#open) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.#moveActive(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.#moveActive(-1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.#setActive(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        const options = this.#options();
        this.#setActive(options.length - 1);
        return;
      }

      if (event.key === 'Enter' && this.#activeIndex >= 0) {
        const active = this.#options()[this.#activeIndex];
        if (!(active instanceof HTMLAnchorElement)) return;
        event.preventDefault();
        active.click();
        return;
      }

      if (event.key !== 'Tab' || isDesktop() || !this.#open) return;

      this.#trapTab(event);
    };

    #query() {
      return this.#input()?.value.trim() ?? '';
    }

    #hasIdleContent() {
      return this.hasAttribute('data-has-idle');
    }

    /* Without popular searches or trending products there is nothing to show
       until the query is long enough to fetch, so the panel stays closed. */
    #canOpen() {
      return this.#hasIdleContent() || this.#query().length >= this.#minChars();
    }

    #syncQueryState() {
      const hasQuery = this.#query() !== '';
      if (hasQuery) this.setAttribute('data-has-query', '');
      else this.removeAttribute('data-has-query');
    }

    #clearQuery() {
      const input = this.#input();
      if (input) input.value = '';
      this.#lastQuery = '';
      this.#invalidateFetches();
      this.#syncQueryState();
      this.#showEmpty();
      this.#resetActive();
      input?.focus();
    }

    #signal() {
      this.#abort ??= new AbortController();
      return this.#abort.signal;
    }

    #abortFetches() {
      this.#abort?.abort();
      this.#abort = null;
    }

    /* Abort in-flight requests and retire their results. The token bump drops
       a count update still waiting; #canPaint drops a suggest response that
       has already arrived, because the query no longer matches. */
    #invalidateFetches() {
      this.#abortFetches();
      window.clearTimeout(this.#timer);
      this.#requestToken += 1;
      this.removeAttribute('aria-busy');
    }

    #cacheKey(query) {
      return query.trim().toLowerCase();
    }

    #remember(query, html) {
      const key = this.#cacheKey(query);

      this.#cache.delete(key);
      this.#cache.set(key, html);

      if (this.#cache.size > CACHE_LIMIT) {
        this.#cache.delete(this.#cache.keys().next().value);
      }
    }

    /* The panel keeps showing whatever it had (popular searches or the last
       results) until new markup lands, rather than blanking while it waits.
       The exception is the no-results view, whose copy quotes the old query. */
    #scheduleFetch() {
      window.clearTimeout(this.#timer);
      const query = this.#query();
      const previous = this.#lastQuery;
      this.#lastQuery = query;

      if (query.length < this.#minChars()) {
        this.#invalidateFetches();
        this.#showEmpty();
        return;
      }

      this.#resolvedQuery = null;

      if (this.getAttribute('data-view') === 'no-results') this.#hidePopular();

      const cached = this.#cache.get(this.#cacheKey(query));

      if (cached !== undefined) {
        // Anything still in flight is now older than this paint.
        const token = ++this.#requestToken;
        this.removeAttribute('aria-busy');
        this.#applyResponse(cached, token, query);
        return;
      }

      // The first fetchable keystroke goes straight out so results can land
      // while the user is still typing; later ones wait for a short pause.
      if (previous.length < this.#minChars()) {
        this.#fetch(query);
        return;
      }

      // Busy for the pause as well as the request, so the field shows it is
      // searching while the previous view is held (or the no-results copy,
      // which quotes the old query, is taken down).
      this.setAttribute('aria-busy', 'true');
      this.#timer = window.setTimeout(() => this.#fetch(query), DEBOUNCE_MS);
    }

    #matchesQuery(query) {
      return this.#cacheKey(this.#query()) === this.#cacheKey(query);
    }

    /* Nothing paints once a newer response has. The response for the query
       in the field always may. An older one may too, when the user has only
       typed further into that same term, so suggestions can land while they
       are still typing. A replaced term must not: the token stays current
       across a debounced keystroke, so it cannot prove the query is unchanged.
       No-results is decided separately, because that view quotes the live query. */
    #canPaint(token, query) {
      if (token <= this.#paintedToken) return false;
      if (this.#matchesQuery(query)) return true;

      const current = this.#cacheKey(this.#query());
      const fetched = this.#cacheKey(query);

      return fetched !== '' && current.startsWith(fetched);
    }

    async #fetch(query) {
      const suggestUrl = this.getAttribute('data-suggest-url');
      if (!suggestUrl) return;

      const token = ++this.#requestToken;
      const url = new URL(suggestUrl, window.location.origin);

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'predictive-search');

      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url.toString(), {
          signal: this.#signal(),
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) return;

        const html = await response.text();

        if (!this.#fragmentFromHtml(html)) return;

        // Worth keeping even when the query has already moved on, so typing
        // back to it paints without another round trip.
        this.#remember(query, html);

        if (!this.#open || !this.#canPaint(token, query)) return;

        this.#applyResponse(html, token, query);
      } catch (error) {
        if (error?.name === 'AbortError') return;
      } finally {
        // A keystroke that only extended or replaced the query leaves this
        // request current until its debounce fires, so keep the spinner up
        // for that next fetch instead of flashing it off.
        if (token === this.#requestToken && this.#matchesQuery(query)) {
          this.removeAttribute('aria-busy');
        }
      }
    }

    #applyResponse(html, token, query) {
      const fragment = this.#fragmentFromHtml(html);
      if (!fragment) return;

      const isEmpty = fragment.hasAttribute('data-predictive-empty-results');

      // "No results" quotes the live query, and it is not true of a query
      // the user has typed past. Only show it for the query in the field.
      if (isEmpty && !this.#matchesQuery(query)) return;

      this.#paintedToken = token;

      if (isEmpty) {
        this.#showNoResults();
        return;
      }

      this.#paintResults(fragment);
    }

    #fragmentFromHtml(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      return template.content.querySelector('.predictive-search__results-body');
    }

    #prefixFetchedIds(root) {
      const prefix = this.#input()?.id;
      if (!root || !prefix) return;

      for (const node of root.querySelectorAll('[id]')) {
        if (!(node instanceof HTMLElement) || node.id === '' || node.id.startsWith(`${prefix}-`)) {
          continue;
        }

        node.id = `${prefix}-${node.id}`;
      }
    }

    #paintResults(fragment) {
      const results = this.#results();
      if (!results) return;

      this.#prefixFetchedIds(fragment);

      const noResults = this.#noResults();
      if (noResults) noResults.hidden = true;

      results.replaceChildren(fragment);
      results.hidden = false;
      this.#hidePopular();
      this.#syncViewAll(true);
      const resultsId = results.getAttribute('id');
      if (resultsId) this.#input()?.setAttribute('aria-controls', resultsId);
      this.#resetActive();
    }

    #showNoResults() {
      const empty = this.#empty();
      const results = this.#results();
      const noResults = this.#noResults();

      this.setAttribute('data-view', 'no-results');

      if (empty) empty.hidden = true;
      if (results) {
        results.hidden = true;
        results.replaceChildren();
      }
      if (noResults) {
        noResults.hidden = false;
        const noResultsId = noResults.getAttribute('id');
        if (noResultsId) this.#input()?.setAttribute('aria-controls', noResultsId);
      }

      this.#fillNoResultsTerms();
      this.#syncViewAll(false);
      this.#resetActive();
      this.#bindCollectionCarousel();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#syncCollectionDots());
      });
    }

    #fillNoResultsTerms() {
      const query = this.#query();

      for (const node of this.querySelectorAll('[data-predictive-no-results-terms]')) {
        node.textContent = query;
      }
    }

    #showEmpty() {
      const results = this.#results();
      const empty = this.#empty();
      const noResults = this.#noResults();

      this.#resolvedQuery = null;
      this.setAttribute('data-view', 'popular');

      if (results) {
        results.hidden = true;
        results.replaceChildren();
      }
      if (noResults) noResults.hidden = true;
      if (empty) {
        empty.hidden = empty.querySelector(OPTION_SELECTOR) == null;
        const emptyId = empty.getAttribute('id');
        if (emptyId && !empty.hidden) this.#input()?.setAttribute('aria-controls', emptyId);
      }
      this.#syncViewAll(false);
      this.#resetActive();

      // Close after reset so a later reopen cannot flash the previous query.
      if (this.#open && !this.#hasIdleContent()) {
        this.#hide(false);
      }
    }

    #hidePopular() {
      const empty = this.#empty();
      const noResults = this.#noResults();

      this.setAttribute('data-view', 'results');
      if (empty) empty.hidden = true;
      if (noResults) noResults.hidden = true;

      const bar = this.#viewAll();
      const wrap = this.#viewAllBar();

      if (bar instanceof HTMLAnchorElement && wrap instanceof HTMLElement && !wrap.hidden) {
        bar.href = this.#resultsUrl();
      }
    }

    #wildcardQuery(query) {
      const trimmed = query.trim();

      if (trimmed === '' || /[*"]/.test(trimmed)) return trimmed;

      return `${trimmed}*`;
    }

    #resultsUrl() {
      const base = this.getAttribute('data-search-url') || '/search';
      const url = new URL(base, window.location.origin);
      const query = this.#resolvedQuery ?? this.#query();

      if (query) url.searchParams.set('q', query);

      url.searchParams.set('type', 'product');

      return `${url.pathname}${url.search}`;
    }

    #viewAllLinks() {
      return [...this.querySelectorAll('.predictive-search__view-all, .predictive-search__view-all-button')].filter(
        (node) => node instanceof HTMLAnchorElement
      );
    }

    #applyViewAllCount(count) {
      const template = this.getAttribute('data-view-all-count') || '';
      const fallback = this.getAttribute('data-view-all-label') || 'View all results';
      const label = template
        ? template.replaceAll('__COUNT__', String(count))
        : `${fallback} (${count})`;

      for (const link of this.#viewAllLinks()) {
        const text = link.querySelector('.button__label');

        if (text instanceof HTMLElement) {
          text.textContent = label;
          continue;
        }

        link.textContent = label;
      }
    }

    async #countFor(query) {
      if (query === '') return null;

      const key = this.#cacheKey(query);

      if (this.#countCache.has(key)) return this.#countCache.get(key);

      const url = new URL(this.getAttribute('data-search-url') || '/search', window.location.origin);

      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', 'search-counts');

      const response = await fetch(url.toString(), {
        signal: this.#signal(),
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) return null;

      const html = await response.text();
      const template = document.createElement('template');

      template.innerHTML = html;

      const count = Number(
        template.content.querySelector('[data-search-counts]')?.getAttribute('data-count')
      );

      if (!Number.isFinite(count)) return null;

      this.#countCache.set(key, count);

      if (this.#countCache.size > CACHE_LIMIT) {
        this.#countCache.delete(this.#countCache.keys().next().value);
      }

      return count;
    }

    async #fetchResultCount() {
      const token = this.#requestToken;
      const raw = this.#query();

      if (raw === '') return;

      this.#resolvedQuery = raw;

      try {
        let count = await this.#countFor(raw);

        if (!this.#open || token !== this.#requestToken) return;

        if (count === 0) {
          const wildcard = this.#wildcardQuery(raw);

          if (wildcard !== raw) {
            const wildCount = await this.#countFor(wildcard);

            if (!this.#open || token !== this.#requestToken) return;

            if (wildCount > 0) {
              this.#resolvedQuery = wildcard;
              count = wildCount;
            }
          }
        }

        for (const link of this.#viewAllLinks()) {
          link.href = this.#resultsUrl();
        }

        if (count != null) this.#applyViewAllCount(count);
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    #syncViewAll(visible) {
      const wrap = this.#viewAllBar();
      const href = this.#resultsUrl();

      for (const link of this.#viewAllLinks()) {
        link.href = href;
      }

      if (!(wrap instanceof HTMLElement)) return;

      if (visible) {
        wrap.hidden = false;
        this.#fetchResultCount();
        return;
      }

      wrap.hidden = true;
    }

    #show() {
      if (this.#dismissed || !this.#canOpen()) return;

      const alreadyOpen = this.#open;

      if (!alreadyOpen) {
        this.#open = true;
        this.setAttribute('data-open', '');
        this.#panel()?.removeAttribute('hidden');
        this.#backdrop()?.removeAttribute('hidden');
        this.#input()?.setAttribute('aria-expanded', 'true');
        this.#header()?.setAttribute('data-search-open', '');
        this.#pinAnnouncement();
        this.#syncDialogMode();
      }

      if (this.#query().length >= this.#minChars()) {
        this.#scheduleFetch();
      } else {
        // Opening onto a short query must not keep the previous term, or the
        // next keystroke that becomes searchable is treated as a continuation
        // and waits out the debounce.
        this.#lastQuery = this.#query();
        this.#showEmpty();
      }

      if (!alreadyOpen) {
        this.dispatchEvent(new CustomEvent(OPEN_EVENT, { bubbles: true }));
      }
    }

    #hide(restoreFocus, clearQuery = false) {
      if (!this.#open) return;

      this.#invalidateFetches();
      // Closing leaves the field as it is. The next time it opens, the first
      // searchable query should go out immediately, not behind the debounce.
      this.#lastQuery = '';

      this.#open = false;
      this.removeAttribute('data-open');
      this.#panel()?.setAttribute('hidden', '');
      this.#backdrop()?.setAttribute('hidden', '');
      this.#input()?.setAttribute('aria-expanded', 'false');
      this.#input()?.removeAttribute('aria-activedescendant');
      this.#teardownDialog();
      this.#unlockScroll();
      this.#unpinAnnouncement();
      this.#resetActive();
      this.#syncViewAll(false);

      if (clearQuery) {
        const input = this.#input();
        if (input) input.value = '';
        this.#syncQueryState();
        this.#showEmpty();
      }

      const header = this.#header();
      if (header && !header.querySelector('predictive-search[data-open]')) {
        header.removeAttribute('data-search-open');
      }

      if (restoreFocus) this.#input()?.focus();
    }

    #syncDialogMode() {
      if (isDesktop()) {
        this.#teardownDialog();
        this.#lockScroll();
        return;
      }

      const label = this.querySelector('.header-search label')?.textContent?.trim() || '';

      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (label) this.setAttribute('aria-label', label);
      this.#lockScroll();
    }

    #teardownDialog() {
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('aria-label');
      this.#panel()?.removeAttribute('role');
      this.#panel()?.removeAttribute('aria-modal');
    }

    #lockScroll() {
      if (this.#scrollLocked) return;
      this.#scrollLocked = true;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    #unlockScroll() {
      if (!this.#scrollLocked) return;
      this.#scrollLocked = false;

      const header = this.#header();
      const menuOpen = header?.hasAttribute('data-mobile-open');
      const overflow = menuOpen ? 'hidden' : '';

      document.documentElement.style.overflow = overflow;
      document.body.style.overflow = overflow;
    }

    #announcementSection() {
      return document.querySelector('.announcement-bar-section');
    }

    #pinAnnouncement() {
      const section = this.#announcementSection();
      const bar = section?.querySelector('.announcement-bar');
      const target = bar instanceof HTMLElement && bar.offsetParent !== null ? bar : section;
      const height = target instanceof HTMLElement ? Math.round(target.getBoundingClientRect().height) : 0;

      document.documentElement.style.setProperty('--announcement-bar-offset', `${height}px`);
    }

    #unpinAnnouncement() {
      const header = this.#header();

      if (header?.querySelector('predictive-search[data-open]')) return;

      document.documentElement.style.removeProperty('--announcement-bar-offset');
    }

    #trapTab(event) {
      const focusable = [
        this.#input(),
        this.querySelector('[data-predictive-clear]'),
        this.querySelector('[data-predictive-close]'),
        ...this.#options(),
        ...this.querySelectorAll('[data-predictive-collection-dot]'),
      ].filter((node) => node instanceof HTMLElement && !node.hidden && node.getClientRects().length > 0);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!this.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    #moveActive(delta) {
      const options = this.#options();
      if (options.length === 0) return;

      const next =
        this.#activeIndex < 0
          ? delta > 0
            ? 0
            : options.length - 1
          : (this.#activeIndex + delta + options.length) % options.length;

      this.#setActive(next);
    }

    #setActive(index) {
      const options = this.#options();
      if (index < 0 || index >= options.length) return;

      this.#resetActiveMarks();
      this.#activeIndex = index;
      const option = options[index];
      option.setAttribute('aria-selected', 'true');
      const id = option.id;
      if (id) this.#input()?.setAttribute('aria-activedescendant', id);
      option.scrollIntoView({ block: 'nearest' });
    }

    #resetActive() {
      this.#activeIndex = -1;
      this.#resetActiveMarks();
      this.#input()?.removeAttribute('aria-activedescendant');
    }

    #resetActiveMarks() {
      for (const option of this.querySelectorAll(`${OPTION_SELECTOR}[aria-selected='true']`)) {
        option.removeAttribute('aria-selected');
      }
    }
  }

  customElements.define(TAG, PredictiveSearch);
})();
